import { useEffect } from 'react';
import { ChevronDown, ChevronRight, Activity as ActivityIcon, Loader2, MessageSquare } from 'lucide-react';
import { usePersistedBoolean } from '../../hooks/usePersistedBoolean';
import { useActivityStore } from '../../store/activityStore';
import { useAuthStore } from '../../store/authStore';
import { useUserStore } from '../../store/userStore';
import { effectiveFirstName, effectiveLastName } from '../../lib/personPrecedence';
import {
  ACTIVITY_ICONS,
  ACTIVITY_COLORS,
  formatRelativeTime,
  formatFullTimestamp,
} from '../activities/activityVisuals';
import type { Activity } from '../../types';

const SECTION_KEY = 'crm:orgPage:activitySection:expanded';

interface OrgActivityFeedProps {
  organizationId: string;
}

// Single feed row — mirrors ActivityTimeline's ActivityItem layout
// (icon circle + summary + relative time), with the contact name shown
// because this is a multi-person feed. Clicking opens the shared detail panel.
const OrgActivityRow = ({
  activity,
  onOpen,
}: {
  activity: Activity;
  onOpen: (id: string) => void;
}) => {
  const Icon = ACTIVITY_ICONS[activity.type];
  const colorClass = ACTIVITY_COLORS[activity.type];
  const contactName = activity.person
    ? `${effectiveFirstName(activity.person)} ${effectiveLastName(activity.person)}`.trim()
    : null;

  return (
    <div
      onClick={() => onOpen(activity.id)}
      className="flex gap-3 px-4 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-md"
    >
      {/* Icon */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header: Type + contact name + time */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize flex-shrink-0">
              {activity.type.toLowerCase().replace('_', ' ')}
            </span>
            {contactName && (
              <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                · {contactName}
              </span>
            )}
          </div>
          <span
            className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0"
            title={formatFullTimestamp(activity.activityAt)}
          >
            {formatRelativeTime(activity.activityAt)}
          </span>
        </div>

        {/* Summary */}
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
          {activity.summary}
        </p>

        {/* Footer: who logged it */}
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          by {activity.createdBy.displayName}
        </p>
      </div>
    </div>
  );
};

/**
 * Org-page "Activity" feed (CRM-26).
 * Rolls up activity across everyone associated with the org. Each row opens
 * the shared activity detail panel. Managers/admins get a rep filter that
 * reuses CRM-15's user-list source (useUserStore). Refetches whenever any
 * activity mutation bumps activityChangeNonce.
 */
export const OrgActivityFeed = ({ organizationId }: OrgActivityFeedProps) => {
  const {
    orgActivities,
    orgActivitiesPagination,
    orgActivitiesLoading,
    orgActivityUserId,
    fetchOrgActivities,
    setOrgActivityRepFilter,
    clearOrgActivities,
    openActivityDetail,
    activityChangeNonce,
  } = useActivityStore();

  const { user } = useAuthStore();
  // Mirror ActivityDetailPanel / authStore role helpers: roles is the canonical
  // multi-role array. The rep filter is managers/admins only.
  const isManager = user?.roles?.some((r) => r === 'MANAGER' || r === 'ADMIN') ?? false;

  const { users, fetchUsers } = useUserStore();

  const [isExpanded, setIsExpanded] = usePersistedBoolean(SECTION_KEY, true);

  // Fetch on mount and whenever any activity mutation bumps the nonce.
  // fetchOrgActivities / clearOrgActivities are stable zustand actions
  // (matches the ActivityTimeline effect which lists its store actions in deps).
  useEffect(() => {
    fetchOrgActivities(organizationId, 1);
    return () => {
      clearOrgActivities();
    };
  }, [organizationId, activityChangeNonce, fetchOrgActivities, clearOrgActivities]);

  // Lazily load the rep list only for managers/admins (same source the
  // Activities page CRM-15 filter uses). fetchUsers no-ops if already loaded.
  useEffect(() => {
    if (isManager) {
      fetchUsers();
    }
  }, [isManager, fetchUsers]);

  const handleLoadMore = () => {
    if (orgActivitiesPagination?.hasNextPage) {
      fetchOrgActivities(organizationId, orgActivitiesPagination.page + 1);
    }
  };

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}
          <ActivityIcon className="h-5 w-5 text-blast-600 dark:text-blast-400 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            Activity
          </h2>
        </div>
        {orgActivitiesPagination && (
          <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
            {orgActivitiesPagination.totalCount}{' '}
            {orgActivitiesPagination.totalCount === 1 ? 'activity' : 'activities'}
          </span>
        )}
      </button>

      {isExpanded && (
        <>
          {/* Rep filter (managers/admins only) */}
          {isManager && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center border-t border-gray-200 dark:border-gray-700 px-5 py-3 bg-gray-50 dark:bg-gray-800/30">
              <select
                value={orgActivityUserId ?? ''}
                onChange={(e) =>
                  setOrgActivityRepFilter(organizationId, e.target.value || null)
                }
                className="input sm:w-auto"
                aria-label="Filter activity by rep"
              >
                <option value="">All reps</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Body */}
          {orgActivitiesLoading && orgActivities.length === 0 ? (
            <div className="flex items-center justify-center py-12 border-t border-gray-200 dark:border-gray-700">
              <Loader2 className="w-6 h-6 text-gray-400 dark:text-gray-500 animate-spin" />
            </div>
          ) : orgActivities.length === 0 ? (
            <div className="text-center py-10 border-t border-gray-200 dark:border-gray-700">
              <MessageSquare className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No activity logged yet</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700 px-1 py-1">
                {orgActivities.map((activity) => (
                  <OrgActivityRow
                    key={activity.id}
                    activity={activity}
                    onOpen={openActivityDetail}
                  />
                ))}
              </div>

              {/* Load more */}
              {orgActivitiesPagination?.hasNextPage && (
                <div className="px-5 py-4 text-center border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={orgActivitiesLoading}
                    className="text-sm text-blast-600 hover:text-blast-700 dark:text-blast-400 dark:hover:text-blast-400 font-medium disabled:opacity-50"
                  >
                    {orgActivitiesLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 inline mr-1 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      `Load more (${orgActivitiesPagination.totalCount - orgActivities.length} remaining)`
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
